import React, { useState, useRef } from "react";
import axios from "axios";
import { GoogleLogin } from "@react-oauth/google";
import { toast } from "react-toastify";
import API_BASE_URL from "../config";
import StarRating from "./StarRating";

const MAX_PHOTOS = 6;
const MAX_PHOTO_MB = 5;

// Same firstName/firstname/given_name fallback chain Header.jsx already
// uses to normalize between normal-login and Google-login user shapes —
// kept in sync with that, not re-derived independently.
const getDisplayName = (user) => {
  if (!user) return "";
  const first = user.firstName || user.firstname || user.given_name || "";
  const last = user.lastName || user.lastname || user.family_name || "";
  return `${first} ${last}`.trim() || user.name || "";
};

const ReviewSubmitForm = ({ stayId, onSubmitted }) => {
  // If the visitor already has a site session, reuse it immediately rather
  // than prompting for Google sign-in again — posting a review should never
  // feel like a second, separate login from the one they already have.
  const existingUser = JSON.parse(localStorage.getItem("user") || "null");
  const existingToken = localStorage.getItem("accessToken");

  const [reviewAuth, setReviewAuth] = useState(
    existingUser && existingToken
      ? { name: getDisplayName(existingUser), token: existingToken }
      : null
  );
  const [guestName, setGuestName] = useState("");
  const [rating, setRating] = useState(0);
  const [reviewText, setReviewText] = useState("");
  const [photos, setPhotos] = useState([]);
  const [photoPreviews, setPhotoPreviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      const res = await axios.post(`${API_BASE_URL}auth/google-login`, {
        token: credentialResponse.credential,
        userType: "user",
      });
      if (res.data?.success !== false) {
        const data = res.data.data || res.data;
        setReviewAuth({
          name: getDisplayName(data),
          token: data.accessToken,
        });
        toast.success("Signed in — posting as yourself now");
      }
    } catch (err) {
      console.error("Google sign-in error:", err);
      toast.error("Google sign-in failed");
    }
  };

  const handlePhotoChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (photos.length + files.length > MAX_PHOTOS) {
      toast.warn(`You can attach up to ${MAX_PHOTOS} photos`);
      return;
    }
    const tooBig = files.find((f) => f.size > MAX_PHOTO_MB * 1024 * 1024);
    if (tooBig) {
      toast.warn(`Each photo must be under ${MAX_PHOTO_MB}MB`);
      return;
    }
    setPhotos((prev) => [...prev, ...files]);
    setPhotoPreviews((prev) => [...prev, ...files.map((f) => URL.createObjectURL(f))]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removePhoto = (index) => {
    URL.revokeObjectURL(photoPreviews[index]);
    setPhotos((prev) => prev.filter((_, i) => i !== index));
    setPhotoPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!rating || !reviewText) {
      toast.warn("Please provide rating and review");
      return;
    }

    try {
      setLoading(true);

      const formData = new FormData();
      formData.append("stay_id", stayId);
      formData.append("rating", rating);
      formData.append("review", reviewText);
      if (!reviewAuth) {
        formData.append("reviewer_name", guestName.trim() || "Guest");
      }
      photos.forEach((file) => formData.append("photos", file));

      const res = await axios.post(`${API_BASE_URL}stays/review`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          ...(reviewAuth ? { Authorization: `Bearer ${reviewAuth.token}` } : {}),
        },
      });

      toast.success("Review submitted successfully");

      setRating(0);
      setReviewText("");
      setGuestName("");
      photoPreviews.forEach((url) => URL.revokeObjectURL(url));
      setPhotos([]);
      setPhotoPreviews([]);

      if (onSubmitted) onSubmitted(res.data?.data);
    } catch (error) {
      console.error("Review submit error:", error);
      toast.error("Failed to submit review");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="review-submit-form">
      <h4>Write a Review</h4>

      <div className="mb-3">
        <label className="review-field-label">Post as</label>
        <div className="review-identity-row">
          {reviewAuth ? (
            <span className="review-posting-as">
              Posting as <strong>{reviewAuth.name || "yourself"}</strong> ✓
            </span>
          ) : (
            <>
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={() => toast.error("Google sign-in failed")}
                theme="filled"
                shape="pill"
                text="signin_with"
                width="240"
              />
              <span className="review-or-sep">or</span>
              <input
                type="text"
                className="form-control review-guest-name"
                placeholder='Your name (optional, defaults to "Guest")'
                value={guestName}
                onChange={(e) => setGuestName(e.target.value)}
              />
            </>
          )}
        </div>
      </div>

      <div className="mb-3">
        <label className="review-field-label">Your Rating</label>
        <StarRating value={rating} interactive size="1.6rem" onChange={setRating} />
      </div>

      <div className="mb-3">
        <label className="review-field-label">Your Review</label>
        <textarea
          className="form-control"
          rows="4"
          value={reviewText}
          onChange={(e) => setReviewText(e.target.value)}
        />
      </div>

      <div className="mb-3">
        <label className="review-field-label">
          Add Photos <span className="review-field-hint">(optional, up to {MAX_PHOTOS})</span>
        </label>
        <div className="review-photo-dropzone">
          {photoPreviews.map((src, i) => (
            <div className="review-photo-thumb" key={src}>
              <img src={src} alt={`attachment ${i + 1}`} />
              <button type="button" className="review-photo-remove" onClick={() => removePhoto(i)}>
                ×
              </button>
            </div>
          ))}
          {photos.length < MAX_PHOTOS && (
            <label className="review-photo-add">
              +
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handlePhotoChange}
                hidden
              />
            </label>
          )}
        </div>
      </div>

      <button
        type="submit"
        className="btn btn-primary"
        disabled={loading}
        style={{ backgroundColor: "#373758", borderColor: "#373758" }}
      >
        {loading ? "Submitting..." : "Submit Review"}
      </button>
    </form>
  );
};

export default ReviewSubmitForm;
